import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import PageHeader from "@replo/design-system/components/PageHeader";

export function StyleExtraction() {
  const [url, setUrl] = React.useState("");

  // NOTE (Berkley 06/24/2025): This is a temporary state to store all extracted colors for testing purposes
  const [colors, setColors] = React.useState<string[]>([]);
  const [fonts, setFonts] = React.useState<[string, string][]>([]);
  const projectId = useCurrentProjectId();

  const utils = trpc.useUtils();
  const { mutateAsync: updateStyleLibrary } =
    trpc.styles.createOrUpdateStyleLibrary.useMutation({
      onSuccess: () => {
        void utils.styles.getStyleLibrary.invalidate();
      },
    });

  const { mutateAsync: updateUrlHeuristics } =
    trpc.styles.createOrUpdateUrlHeuristics.useMutation({
      onSuccess: () => {
        void utils.styles.getUrlHeuristics.invalidate();
      },
    });

  const { mutateAsync: updateComponentsCss } =
    trpc.styles.createOrUpdateComponentsCss.useMutation({
      onSuccess: () => {
        void utils.styles.getComponentsCss.invalidate();
      },
    });

  const {
    mutateAsync: extractStyles,
    isPending: isLoadingStyles,
    data: extractedData,
  } = trpc.styles.extractStyles.useMutation({
    onSuccess: async (data) => {
      setColors(data.styles.colors);
      setFonts(data.styles.uploadedFonts ?? []);

      if (data.styles.styleLibrary?.colors && projectId) {
        await updateStyleLibrary({
          projectId,
          styleLibrary: data.styles.styleLibrary,
        });
      }

      if (data.styles.variantsByTag && projectId) {
        await updateUrlHeuristics({
          projectId,
          urlHeuristics: JSON.stringify(data.styles.variantsByTag),
        });
      }

      if (data.styles.componentsCss && projectId) {
        await updateComponentsCss({
          projectId,
          componentsCss: data.styles.componentsCss,
        });
      }

      if (projectId) {
        void utils.webContainer.getFileTree.invalidate({ projectId });
      }
    },
    onError: (error) => {
      console.error("Error extracting colors:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    if (!projectId) {
      return;
    }
    e.preventDefault();
    if (!url) {
      return;
    }
    await extractStyles({ url, projectId });
  };

  // Use the extracted data directly or provide fallback
  const styleLibrary: StyleLibrary = extractedData?.styles.styleLibrary ?? {
    colors: {
      light: "",
      subtle: "",
      muted: "",
      dark: "",
      primary: "",
      secondary: "",
      tertiary: "",
      accent: "",
    },
    typography: {
      h1: { name: "", url: "", weight: 700, size: 48 },
      h2: { name: "", url: "", weight: 500, size: 32 },
      h3: { name: "", url: "", weight: 500, size: 28 },
      h4: { name: "", url: "", weight: 500, size: 24 },
      body: { name: "", url: "", weight: 400, size: 16 },
    },
  };

  const fontFaceCSS = fonts
    .map(
      ([name, url]) => `
      @font-face {
        font-family: '${name}';
        src: url('${url}') format('woff2'), url('${url}') format('woff');
        font-display: swap;
      }
    `,
    )
    .join("");

  // Create CSS for StyleLibrary fonts
  const styleLibraryFontCSS = Object.values(styleLibrary.typography)
    .filter((font) => font.name && font.url)
    .map(
      (font) => `
      @font-face {
        font-family: '${font.name}';
        src: url('${font.url}') format('woff2'), url('${font.url}') format('woff');
        font-display: swap;
      }
    `,
    )
    .join("");

  return (
    <div className="flex flex-col w-full h-screen overflow-hidden gap-6">
      {/* Add font-face styles for both extracted fonts and StyleLibrary fonts */}
      {(fontFaceCSS || styleLibraryFontCSS) && (
        <style
          dangerouslySetInnerHTML={{
            __html: fontFaceCSS + styleLibraryFontCSS,
          }}
        />
      )}

      <PageHeader title="Style Extraction" />
      <div className="flex-1 min-h-0">
        <div className="flex items-stretch gap-6 h-full">
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="flex flex-col gap-4 max-w-md"
          >
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-default">URL</span>
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                isRequired
              />
            </label>

            <Button
              type="submit"
              variant="primary"
              layoutClassName="w-fit"
              isLoading={isLoadingStyles}
            >
              Submit
            </Button>
          </form>

          {(Object.values(styleLibrary.colors).some((color) => color) ||
            fonts.length > 0) && (
            <div className="flex flex-col gap-8 overflow-y-auto flex-1 min-h-0">
              {Object.values(styleLibrary.colors).some((color) => color) && (
                <div>
                  <h2 className="text-lg font-semibold text-default mb-4">
                    Color Scheme
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {Object.entries(styleLibrary.colors).map(
                      ([role, color]) =>
                        color && (
                          <div
                            key={role}
                            className="border border-subtle rounded-lg p-4 flex flex-col items-center gap-2"
                          >
                            <div
                              className="w-16 h-16 rounded-md border border-muted"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-sm font-medium text-default capitalize">
                              {role}
                            </span>
                            <span className="text-xs text-muted font-mono text-center break-all">
                              {color}
                            </span>
                          </div>
                        ),
                    )}
                  </div>
                </div>
              )}

              {/* Display fonts from StyleLibrary */}
              {(() => {
                // Group typography levels by font name
                const fontGroups: Record<string, string[]> = {};
                Object.entries(styleLibrary.typography).forEach(
                  ([level, font]) => {
                    if (font.name) {
                      if (!fontGroups[font.name]) {
                        fontGroups[font.name] = [];
                      }
                      fontGroups[font.name]!.push(level);
                    }
                  },
                );

                return Object.keys(fontGroups).length > 0 ? (
                  <div>
                    <h2 className="text-lg font-semibold text-default mb-4">
                      Typography Assignments
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(fontGroups).map(([fontName, levels]) => (
                        <div
                          key={fontName}
                          className="border border-subtle rounded-lg p-4 flex flex-col gap-3"
                        >
                          <div
                            className="text-2xl font-normal text-center"
                            style={{ fontFamily: `'${fontName}', sans-serif` }}
                          >
                            {fontName}
                          </div>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {levels.map((level) => (
                              <span
                                key={level}
                                className="text-xs bg-subtle text-muted px-2 py-1 rounded-md uppercase font-medium"
                              >
                                {level}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* NOTE (Berkley 06/24/2025): This is a temporary section to display all extracted colors for testing purposes */}
              {(() => {
                const usedColors = new Set(
                  Object.values(styleLibrary.colors).filter(Boolean),
                );
                const otherColors = colors.filter(
                  (color) => !usedColors.has(color),
                );
                return otherColors.length > 0 ? (
                  <div>
                    <h2 className="text-lg font-semibold text-default mb-4">
                      (DEBUG) Other Extracted Colors
                    </h2>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {otherColors.map((color, index) => (
                        <div
                          key={index}
                          className="border border-subtle rounded-lg p-3 flex flex-col items-center gap-2"
                        >
                          <div
                            className="w-12 h-12 rounded-md border border-muted"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-xs text-muted font-mono text-center break-all">
                            {color}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {fonts.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-default mb-4">
                    Extracted Fonts
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fonts.map(([name], index) => (
                      <div
                        key={index}
                        className="border border-subtle rounded-lg p-4 flex flex-col gap-2"
                      >
                        <div
                          className="text-2xl font-normal text-center"
                          style={{ fontFamily: `'${name}', sans-serif` }}
                        >
                          {name}
                        </div>
                        <span className="text-sm text-muted text-center break-all">
                          {name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
